import React, { useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import restaurantAPI from '../../../api/restaurantAPI';
import { ProductGrid } from '../ProductGrid';
import { OrderCart } from '../OrderCart';
import { useRestaurantCart } from '../../../hooks/useRestaurantCart';
import PinPad from '../PinPad';

const STATUS_STYLE = {
  available: 'border-green-500 bg-green-100 text-gray-900',
  occupied: 'border-amber-500 bg-amber-100 text-gray-900',
  reserved: 'border-blue-500 bg-blue-100 text-gray-900',
  'needs-attention': 'border-red-500 bg-red-100 text-gray-900',
  bill_requested: 'border-orange-300 bg-orange-50 text-orange-400 opacity-60 cursor-not-allowed',
  merged: 'border-gray-300 bg-gray-100 text-gray-400 opacity-60 cursor-not-allowed'
};

const statusOf = (table) => String(table?.status || table?.tableStatus || 'available').toLowerCase();

export const NewOrderModal = ({
  isOpen,
  onClose,
  tables = [],
  waiters = [],
  products = [],
  onOrderSent,
  loading
}) => {
  const [step, setStep] = useState(1);
  const [transactionType, setTransactionType] = useState('dine-in');
  const [customerName, setCustomerName] = useState('');
  const [selectedWaiterId, setSelectedWaiterId] = useState('');
  const [waiterPin, setWaiterPin] = useState('');
  const [verifiedWaiter, setVerifiedWaiter] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [authError, setAuthError] = useState(null);
  const [localLoading, setLocalLoading] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [lastSentSummary, setLastSentSummary] = useState({ total: 0, kitchen: 0, bar: 0 });
  const [showAddTableForm, setShowAddTableForm] = useState(false);
  const [newTableData, setNewTableData] = useState({ tableNumber: '', seats: 2 });
  const [showSendPinPrompt, setShowSendPinPrompt] = useState(false);
  const [sendPin, setSendPin] = useState('');
  const [sendPinError, setSendPinError] = useState(null);

  const { getAccessToken, loading: authLoading } = useAuth();
  const modalCart = useRestaurantCart('modal-order', transactionType);

  const selectedTable = useMemo(() => tables.find((table) => table._id === selectedTableId), [tables, selectedTableId]);

  console.log('📊 NewOrderModal - tables:', tables.length, 'waiters:', waiters.length, 'products:', products.length);

  if (!isOpen) return null;

  const totalLoading = loading || localLoading;

  const resetAndClose = () => {
    setStep(1);
    setTransactionType('dine-in');
    setCustomerName('');
    setSelectedWaiterId('');
    setWaiterPin('');
    setVerifiedWaiter(null);
    setSelectedTableId('');
    setAuthError(null);
    setOrderId(null);
    setLastSentSummary({ total: 0, kitchen: 0, bar: 0 });
    setShowAddTableForm(false);
    setNewTableData({ tableNumber: '', seats: 2 });
    setShowSendPinPrompt(false);
    setSendPin('');
    setSendPinError(null);
    modalCart.clearCart();
    onClose?.();
  };

  const createNewTable = async () => {
    if (!newTableData.tableNumber || !newTableData.seats) {
      setAuthError('Please enter table number and seats');
      return;
    }

    // Guard: wait for the silent-refresh boot to finish before firing a write.
    // This prevents 401 on hard refresh when the user clicks Add Table fast.
    if (authLoading) {
      setAuthError('Still loading. Please wait a moment and try again.');
      return;
    }

    const token = getAccessToken();
    if (!token) {
      try {
        await fetch('/api/auth/refresh', { method: 'POST', credentials: 'include' });
      } catch {}
      if (!getAccessToken()) {
        setAuthError('Session expired. Please ask the manager to log in again.');
        return;
      }
    }

    try {
      setLocalLoading(true);
      setAuthError(null);

      const response = await restaurantAPI.tables.create({
        tableNumber: String(newTableData.tableNumber),
        seats: Number(newTableData.seats),
        type: 'regular',
        section: 'indoor'
      });

      if (response.data?.success) {
        // The new table is created, close the form and refresh tables
        setShowAddTableForm(false);
        setNewTableData({ tableNumber: '', seats: 2 });

        // Reload tables in the background so newly added table state is fresh for the next open.
        await restaurantAPI.tables.getAll();

        // You would need to pass an update function from parent, but for now we'll just select it
        setSelectedTableId(response.data.table._id);
        setStep(3); // Move to order step
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        setAuthError('Not authorized to add tables from this terminal. Manager login required.');
      } else {
        setAuthError(error.response?.data?.message || error.message || 'Failed to create table');
      }
    } finally {
      setLocalLoading(false);
    }
  };

  const verifyWaiter = async (pinFromPad) => {
    if (!selectedWaiterId) {
      setAuthError('Please select waiter');
      return;
    }

    const pinString = String(pinFromPad || waiterPin).trim();
    if (pinString.length !== 4 || !/^\d{4}$/.test(pinString)) {
      setAuthError('PIN must be exactly 4 digits');
      return;
    }

    try {
      setLocalLoading(true);
      setAuthError(null);
    
      const response = await restaurantAPI.shifts.verifyWaiter(selectedWaiterId, pinString);
      
      console.log('🎯 Waiter response:', response.data);
      
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Invalid waiter credentials');
      }

      const matched = waiters.find((waiter) => waiter._id === selectedWaiterId);
      setVerifiedWaiter(matched || response.data.user || null);
      setWaiterPin('');

      // Skip table selection for takeout/delivery
      if (transactionType === 'dine-in') {
        setStep(2);
      } else {
        setStep(3);
      }
    } catch (error) {
      setWaiterPin('');
      setAuthError(error.response?.data?.message || error.message || 'Authentication failed');
    } finally {
      setLocalLoading(false);
    }
  };

  const openSendPrompt = () => {
    const requiresTable = transactionType === 'dine-in';
    if (requiresTable && !selectedTableId) {
      setAuthError('Please select a table for dine-in orders');
      return;
    }

    if (modalCart.items.length === 0 || !verifiedWaiter?._id) {
      setAuthError('Cart is empty or waiter not verified');
      return;
    }

    setSendPin('');
    setSendPinError(null);
    setShowSendPinPrompt(true);
  };

  const sendToKitchen = async (confirmationPin) => {
    // For dine-in, require table. For takeout/delivery, table is optional (can be null/virtual)
    const requiresTable = transactionType === 'dine-in';
    if (requiresTable && !selectedTableId) {
      setAuthError('Please select a table for dine-in orders');
      return;
    }
    
    if (modalCart.items.length === 0 || !verifiedWaiter?._id) {
      setAuthError('Cart is empty or waiter not verified');
      return;
    }

    try {
      setLocalLoading(true);
      setAuthError(null);

      const pinString = String(confirmationPin).trim();

      if (pinString.length !== 4 || !/^\d{4}$/.test(pinString)) {
        setSendPinError('PIN must be exactly 4 digits');
        return;
      }

      // Re-enter PIN before sending to prevent sending orders under another waiter session.
      const verifyResponse = await restaurantAPI.shifts.verifyWaiter(verifiedWaiter._id, pinString);
      const pinVerifiedWaiterId = verifyResponse?.data?.user?._id;

      if (!verifyResponse?.data?.success || !pinVerifiedWaiterId) {
        setSendPinError('Incorrect PIN');
        return;
      }

      if (String(pinVerifiedWaiterId) !== String(verifiedWaiter._id)) {
        setSendPinError('You can only send your own orders');
        return;
      }

      setSendPin('');
      setSendPinError(null);
      setShowSendPinPrompt(false);
      
      const productsPayload = modalCart.items.map((item) => ({
        productId: item.productId,
        quantity: item.quantity,
        price: item.price,
        note: item.notes
      }));

      let activeOrderId = orderId;

      if (!activeOrderId) {
        // For takeout/delivery without table, use a virtual table or pass customer name
        const tableIdOrName = selectedTableId || customerName || 'Takeout';
        const created = await modalCart.createOrder(tableIdOrName, verifiedWaiter._id, pinString);
        const data = created?.data?.order || created?.order || created?.data;
        activeOrderId = data?._id || data?.id;

        if (!activeOrderId) {
          throw new Error('Order created but ID missing');
        }
      } else {
        await restaurantAPI.orders.addItems(activeOrderId, productsPayload);
      }

      await restaurantAPI.orders.send(activeOrderId);
      setOrderId(activeOrderId);
      onOrderSent?.(activeOrderId, selectedTableId);

      const barItems = modalCart.items.reduce((sum, item) => {
        const category = String(item.productCategory || '').toLowerCase();
        const isBarItem = category.includes('drink') || category.includes('beverage') || category.includes('bar');
        return sum + (isBarItem ? Number(item.quantity || 0) : 0);
      }, 0);
      const totalItems = modalCart.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
      setLastSentSummary({
        total: totalItems,
        bar: barItems,
        kitchen: Math.max(totalItems - barItems, 0)
      });

      modalCart.clearCart();
      setStep(4);
    } catch (sendError) {
      setSendPinError(sendError.response?.data?.message || sendError.message || 'Failed to send order');
    } finally {
      setLocalLoading(false);
    }
  };

  const blockedTable = (table) => {
    const status = statusOf(table);
    return status === 'bill_requested' || status === 'merged';
  };

  const handleTableClick = (table) => {
    const tableId = table?._id;
    if (!tableId || blockedTable(table)) {
      return;
    }

    const status = statusOf(table);
    if (status === 'reserved') {
      const proceed = window.confirm('This table is reserved. Continue anyway?');
      if (!proceed) {
        return;
      }
    }

    setSelectedTableId(tableId);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className={`w-full rounded-xl bg-white shadow-2xl ${step === 3 ? 'max-w-4xl' : 'max-w-md'}`}>
        <div className="border-b border-gray-200 px-6 py-4">
          <p className="text-sm font-medium text-gray-500">
            {transactionType === 'dine-in' 
              ? '① Waiter → ② Table → ③ Order → ④ Done' 
              : '① Waiter → ② Order → ③ Done'}
          </p>
        </div>

        {step === 1 && (
          <div className="space-y-4 p-6">
            <h3 className="text-lg font-bold text-gray-900">Order Type & Waiter</h3>
            
            {/* Transaction Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setTransactionType('dine-in')}
                  className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                    transactionType === 'dine-in'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  🍽️ Dine-In
                </button>
                <button
                  type="button"
                  onClick={() => setTransactionType('takeout')}
                  className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                    transactionType === 'takeout'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  📦 Takeout
                </button>
                <button
                  type="button"
                  onClick={() => setTransactionType('delivery')}
                  className={`px-4 py-3 rounded-lg border-2 font-medium text-sm transition-colors ${
                    transactionType === 'delivery'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  🚗 Delivery
                </button>
              </div>
            </div>

            {/* Customer Name for Takeout/Delivery */}
            {(transactionType === 'takeout' || transactionType === 'delivery') && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                />
              </div>
            )}

            {/* Waiter Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Waiter</label>
              <select
                value={selectedWaiterId}
                onChange={(event) => setSelectedWaiterId(event.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2"
              >
                <option value="">Select waiter</option>
                {waiters.map((waiter) => (
                  <option key={waiter._id} value={waiter._id}>
                    {waiter.name || waiter.username || waiter.email}
                  </option>
                ))}
              </select>
            </div>

            <PinPad value={waiterPin} onChange={setWaiterPin} onSubmit={verifyWaiter} error={authError} />

            <button
              type="button"
              onClick={() => verifyWaiter(waiterPin)}
              disabled={totalLoading || !selectedWaiterId || waiterPin.length !== 4 || 
                       ((transactionType === 'takeout' || transactionType === 'delivery') && !customerName.trim())}
              className="w-full rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {totalLoading ? 'Verifying...' : 'Next'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 p-6">
            <h3 className="text-lg font-bold text-gray-900">Select a table</h3>
            
            {tables.length === 0 ? (
              <div className="rounded-lg border border-orange-300 bg-orange-50 p-4 text-center">
                <p className="mb-3 text-sm text-gray-700">No tables available yet.</p>
                <button
                  type="button"
                  onClick={() => setShowAddTableForm(true)}
                  className="w-full rounded-lg bg-blue-600 px-4 py-2 font-semibold text-white hover:bg-blue-700"
                >
                  Create a New Table
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-2">
                  {tables.map((table) => {
                    const tableId = table._id;
                    const tableNumber = table.number || table.tableNumber || table.name || '-';
                    const status = statusOf(table);
                    const isBlocked = blockedTable(table);
                    const selected = selectedTableId === tableId;

                    return (
                      <button
                        key={tableId}
                        type="button"
                        onClick={() => handleTableClick(table)}
                        disabled={isBlocked}
                        className={`rounded-lg border px-2 py-3 text-sm font-semibold ${STATUS_STYLE[status] || STATUS_STYLE.available} ${selected ? 'ring-2 ring-blue-500' : ''}`}
                      >
                        T{tableNumber}
                      </button>
                    );
                  })}
                </div>
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => setShowAddTableForm(true)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium hover:bg-gray-50"
                  >
                    + Add New Table
                  </button>
                </div>
              </>
            )}

            {showAddTableForm && (
              <div className="rounded-lg border border-blue-300 bg-blue-50 p-4 space-y-3">
                <h4 className="font-semibold text-gray-900">Create New Table</h4>
                <input
                  type="text"
                  placeholder="Table Number (e.g., 5, 6A)"
                  value={newTableData.tableNumber}
                  onChange={(e) => setNewTableData({ ...newTableData, tableNumber: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <input
                  type="number"
                  min="1"
                  max="20"
                  placeholder="Number of Seats"
                  value={newTableData.seats}
                  onChange={(e) => setNewTableData({ ...newTableData, seats: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddTableForm(false)}
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={createNewTable}
                    disabled={totalLoading || authLoading || !newTableData.tableNumber || !newTableData.seats}
                    className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    {authLoading ? 'Loading...' : 'Create Table'}
                  </button>
                </div>
              </div>
            )}

            {!showAddTableForm && (
              <div className="flex justify-between gap-2">
                <button type="button" onClick={() => setStep(1)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">Back</button>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  disabled={!selectedTableId}
                  className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                >
                  Confirm Table
                </button>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="p-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">
                {transactionType === 'dine-in' 
                  ? `Table ${selectedTable?.number || selectedTable?.tableNumber || '-'} — Waiter: ${verifiedWaiter?.name || verifiedWaiter?.username || 'Waiter'}`
                  : `${transactionType === 'takeout' ? '📦 Takeout' : '🚗 Delivery'}: ${customerName || 'Walk-in'} — Waiter: ${verifiedWaiter?.name || verifiedWaiter?.username || 'Waiter'}`
                }
              </h3>
            </div>

            {authError && <p className="mb-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{authError}</p>}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" style={{ minHeight: '460px' }}>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <ProductGrid
                  products={products}
                  onSelectProduct={modalCart.addItem}
                  loading={totalLoading}
                  cartItems={modalCart.items}
                />
              </div>
              <div className="overflow-hidden rounded-lg border border-gray-200">
                <OrderCart
                  items={modalCart.items}
                  onUpdateQuantity={modalCart.updateQuantity}
                  onRemoveItem={modalCart.removeItem}
                  onUpdateNotes={modalCart.updateItemNotes}
                  onClearCart={modalCart.clearCart}
                  loading={totalLoading}
                  adminMode={false}
                />
              </div>
            </div>

            <div className="mt-3 flex justify-between gap-2">
              <button 
                type="button" 
                onClick={() => setStep(transactionType === 'dine-in' ? 2 : 1)} 
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                Back
              </button>
              <button
                type="button"
                onClick={openSendPrompt}
                disabled={totalLoading || modalCart.items.length === 0}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Send to Kitchen / Bar
              </button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="space-y-4 p-6 text-center">
            <h3 className="text-xl font-bold text-green-700">✅ Order Sent!</h3>
            <p className="text-sm text-gray-700">
              {transactionType === 'dine-in' 
                ? `Table ${selectedTable?.number || selectedTable?.tableNumber || '-'} — ${lastSentSummary.total} items`
                : `${transactionType === 'takeout' ? 'Takeout' : 'Delivery'} for ${customerName || 'Walk-in'} — ${lastSentSummary.total} items`
              }
            </p>
            <p className="text-sm text-gray-700">Kitchen: {lastSentSummary.kitchen} items</p>
            <p className="text-sm text-gray-700">Bar: {lastSentSummary.bar} items</p>
            <p className="text-xs text-gray-500">Order #{String(orderId || '').slice(-6) || '—'}</p>
            <div className="flex justify-center gap-2">
              <button
                type="button"
                onClick={() => setStep(3)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium"
              >
                Add More Items
              </button>
              <button
                type="button"
                onClick={resetAndClose}
                className="rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>

      {showSendPinPrompt && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
          <div className="w-full max-w-xs rounded-xl bg-white p-5 shadow-2xl">
            <h4 className="text-base font-bold text-gray-900">Confirm waiter PIN</h4>
            <p className="mt-1 text-xs text-gray-500">
              Enter PIN to send order for {verifiedWaiter?.name || verifiedWaiter?.username || 'waiter'}
            </p>

            <div className="mt-4">
              <PinPad value={sendPin} onChange={setSendPin} onSubmit={sendToKitchen} error={sendPinError} />
            </div>

            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowSendPinPrompt(false);
                  setSendPin('');
                  setSendPinError(null);
                }}
                disabled={totalLoading}
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => sendToKitchen(sendPin)}
                disabled={totalLoading || sendPin.length !== 4}
                className="flex-1 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {totalLoading ? 'Sending...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewOrderModal;
